import React from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Camera,
  Clock,
  FileText,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
} from "lucide-react";

const galleryImages = [
  {
    url: "https://customer-assets.emergentagent.com/job_07e0f115-becc-437e-be41-89e1bceeef3a/artifacts/bp4ir245_DJI_0005.jpg",
    alt: "Overhead roof overview with shingles",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_07e0f115-becc-437e-be41-89e1bceeef3a/artifacts/9btb1tqq_DJI_0012.jpg",
    alt: "Roof slope detail captured from drone",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_07e0f115-becc-437e-be41-89e1bceeef3a/artifacts/t9jpgbfq_DJI_0015.jpg",
    alt: "Wide roof surface inspection image",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_07e0f115-becc-437e-be41-89e1bceeef3a/artifacts/926uh67g_DJI_0016_stitchturnedepoupbigSHARP.jpg",
    alt: "Full roof stitch showing coverage",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_cascade-roofs/artifacts/zdp6w8bs_DJI_0006edit.jpg",
    alt: "Shingled roof overview with dark ridge detail",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_cascade-roofs/artifacts/d01hvpea_DJI_0026.jpg",
    alt: "Close roof ridge detail with patch area",
  },
];

const heroImages = {
  primary: {
    url: "https://customer-assets.emergentagent.com/job_cascade-roofs/artifacts/q7o6msqp_DJI_0028.jpg",
    alt: "Flag and river aerial view at sunset over Bend",
  },
};

const services = [
  {
    title: "Aerial Roof Imaging for Contractors",
    description:
      "High-precision overhead captures that eliminate ladder time and speed up scoping.",
    icon: Camera,
    span: "md:col-span-2",
  },
  {
    title: "High-resolution overhead roof photos",
    description:
      "Pin-sharp imagery tailored for measurements, materials, and condition assessment.",
    icon: FileText,
  },
  {
    title: "Documentation for estimates & insurance claims",
    description:
      "Clear evidence packages to support estimates, repairs, and claim submissions.",
    icon: FileText,
  },
  {
    title: "FAA compliant, licensed & insured",
    description:
      "Operate with confidence knowing we are fully FAA compliant, licensed, and insured.",
    icon: ShieldCheck,
  },
  {
    title: "Fast turnaround",
    description:
      "Deliverables typically in 24-48 hours so your projects never wait.",
    icon: Clock,
  },
  {
    title: "Serving Bend & Central Oregon",
    description:
      "Local expertise with regional conditions that impact roofing decisions.",
    icon: MapPin,
    span: "md:col-span-2",
  },
];

const LandingPage = () => {
  return (
    <div className="relative bg-white text-slate-900 overflow-hidden" data-testid="landing-page">
      <div className="grain-overlay" data-testid="grain-overlay" />

      <header className="relative z-10" data-testid="site-header">
        <nav
          className="container mx-auto px-4 md:px-6 max-w-7xl flex items-center justify-between py-6"
          data-testid="primary-navigation"
        >
          <div className="flex items-center gap-3" data-testid="brand-block">
            <div className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-bold"
              data-testid="brand-mark">
              CR
            </div>
            <div>
              <p className="text-lg font-semibold" data-testid="brand-name">
                Cascade Roof Imaging
              </p>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="brand-tagline">
                Aerial Roof Imaging
              </p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm font-semibold" data-testid="nav-links">
            <a
              href="#services"
              className="text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="nav-services-link"
            >
              Services
            </a>
            <a
              href="#gallery"
              className="text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="nav-gallery-link"
            >
              Gallery
            </a>
            <a
              href="#contact"
              className="text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="nav-contact-link"
            >
              Contact
            </a>
          </div>
          <a
            href="mailto:info@cascaderoofimaging.com?subject=Cascade%20Roof%20Imaging%20Request"
            className="hidden md:inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6 py-3 font-semibold shadow-lg shadow-violet-500/20 transition-all"
            data-testid="nav-request-info-button"
          >
            Request Info <ArrowUpRight size={18} />
          </a>
        </nav>
      </header>

      <main className="relative z-10" data-testid="main-content">
        <section className="relative pt-12 pb-24 md:pt-20 md:pb-32" data-testid="hero-section">
          <div className="hero-glow" data-testid="hero-glow" />
          <div className="container mx-auto px-4 md:px-6 max-w-7xl space-y-12">
            <motion.div
              className="relative rounded-3xl overflow-hidden shadow-2xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              data-testid="hero-banner"
            >
              <img
                src={heroImages.primary.url}
                alt={heroImages.primary.alt}
                className="w-full h-[320px] md:h-[440px] object-cover"
                data-testid="hero-banner-image"
              />
              <div
                className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-900/10 to-transparent"
                data-testid="hero-banner-overlay"
              />
              <div className="absolute bottom-6 left-6 md:bottom-8 md:left-8" data-testid="hero-banner-text">
                <p
                  className="text-xs uppercase tracking-[0.4em] text-white/80"
                  data-testid="hero-banner-eyebrow"
                >
                  Cascade Roof Imaging
                </p>
                <p className="text-lg md:text-2xl font-semibold text-white" data-testid="hero-banner-location">
                  Bend & Central Oregon
                </p>
              </div>
            </motion.div>
            <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] items-start">
              <div className="space-y-8" data-testid="hero-text-block">
                <p
                  className="text-sm uppercase tracking-[0.35em] font-bold text-violet-600"
                  data-testid="hero-eyebrow"
                >
                  Cascade Roof Imaging
                </p>
                <h1
                  className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.02]"
                  data-testid="hero-title"
                >
                  Photo Documentation for Roofing Contractors.
                </h1>
                <p
                  className="text-base md:text-lg leading-relaxed text-slate-600 max-w-xl"
                  data-testid="hero-description"
                >
                 Fast roof photo documentation for estimates, inspections, and insurance claims.
                </p>
                <div className="flex flex-wrap items-center gap-4" data-testid="hero-cta-group">
                  <a
                    href="mailto:info@cascaderoofimaging.com?subject=Cascade%20Roof%20Imaging%20Request"
                    className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 py-4 font-semibold shadow-lg shadow-violet-500/20 transition-all hover:scale-[1.02] active:scale-95 inline-flex items-center gap-2"
                    data-testid="hero-request-info-button"
                  >
                    Request Info <ArrowUpRight size={18} />
                  </a>
                  <a
                    href="#gallery"
                    className="bg-white text-slate-900 border border-slate-200 hover:border-violet-200 hover:bg-slate-50 rounded-full px-8 py-4 font-semibold transition-all"
                    data-testid="hero-view-gallery-button"
                  >
                    View Sample Work
                  </a>
                </div>
                <div className="flex flex-wrap gap-6 text-sm font-semibold text-slate-700" data-testid="hero-contact-row">
                  <a
                    href="tel:458-600-6028"
                    className="inline-flex items-center gap-2"
                    data-testid="hero-phone-link"
                  >
                    <Phone size={18} className="text-violet-600" /> 458-600-6028
                  </a>
                  <a
                    href="mailto:info@cascaderoofimaging.com"
                    className="inline-flex items-center gap-2"
                    data-testid="hero-email-link"
                  >
                    <Mail size={18} className="text-violet-600" /> info@cascaderoofimaging.com
                  </a>
                </div>
                <div
                  className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.25em] text-slate-500"
                  data-testid="hero-highlights"
                >
                  <span data-testid="hero-highlight-faa">FAA Compliant</span>
                  <span data-testid="hero-highlight-licensed">Licensed & Insured</span>
                  <span data-testid="hero-highlight-turnaround">Fast Turnaround</span>
                </div>
              </div>
              <div className="space-y-6" data-testid="hero-info-column">
                <div
                  className="bg-slate-950 text-white rounded-2xl p-6 shadow-xl"
                  data-testid="intro-price-card"
                >
                  <p
                    className="text-xs uppercase tracking-[0.35em] text-sky-200"
                    data-testid="intro-price-label"
                  >
                    Introductory Price
                  </p>
                  <p className="text-4xl font-semibold mt-3" data-testid="intro-price-value">
                    $149.00*
                  </p>
                  <p className="text-sm text-slate-200 mt-3" data-testid="intro-price-description">
                    Flat-rate residential documentation package delivered within 24 hours.
                  </p>
                </div>
                <div
                  className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm"
                  data-testid="hero-facts-card"
                >
                  <p
                    className="text-xs uppercase tracking-[0.35em] text-slate-500"
                    data-testid="hero-facts-label"
                  >
                    Coverage Highlights
                  </p>
                  <ul className="mt-4 space-y-3 text-slate-700" data-testid="hero-facts-list">
                    <li className="flex items-start gap-3" data-testid="hero-fact-0">
                      <span className="h-2 w-2 rounded-full bg-violet-500 mt-2" />
                      Local crews serving Bend & Central Oregon.
                    </li>
                    <li className="flex items-start gap-3" data-testid="hero-fact-1">
                      <span className="h-2 w-2 rounded-full bg-violet-500 mt-2" />
                      Fast turnaround and flexible scheduling.
                    </li>
                    <li className="flex items-start gap-3" data-testid="hero-fact-2">
                      <span className="h-2 w-2 rounded-full bg-violet-500 mt-2" />
                      FAA compliant, licensed, and insured operations.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="services" className="py-20 md:py-28 bg-slate-50" data-testid="services-section">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl space-y-12">
            <div className="space-y-4" data-testid="services-header">
              <p className="text-sm uppercase tracking-[0.35em] font-bold text-violet-600" data-testid="services-eyebrow">
                Services
              </p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight" data-testid="services-title">
                Imaging packages built for fast, accurate decisions.
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl" data-testid="services-description">
                Each project is designed to deliver actionable documentation for estimating, repairs, and claims.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="services-grid">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={service.title}
                    className={`group bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-xl hover:border-violet-100 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden ${
                      service.span || ""
                    }`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    data-testid={`service-card-${index}`}
                  >
                    <div className="flex items-center gap-3 text-violet-600" data-testid={`service-icon-${index}`}>
                      <Icon size={24} />
                      <span className="text-xs uppercase tracking-[0.3em] font-semibold text-slate-500" data-testid={`service-label-${index}`}>
                        Signature
                      </span>
                    </div>
                    <h3 className="text-2xl font-semibold mt-6 mb-3" data-testid={`service-title-${index}`}>
                      {service.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed" data-testid={`service-description-${index}`}>
                      {service.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section id="gallery" className="py-20 md:py-28" data-testid="gallery-section">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl space-y-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-testid="gallery-header">
              <div className="space-y-4" data-testid="gallery-header-text">
                <p className="text-sm uppercase tracking-[0.35em] font-bold text-violet-600" data-testid="gallery-eyebrow">
                  Gallery
                </p>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-tight" data-testid="gallery-title">
                  Sample roof photo documentation
                </h2>
              </div>
              <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-lg" data-testid="gallery-description">
                Each project includes complete roof photo documentation with overview images, slope coverage, and detail shots for estimates, inspections, and insurance claims.
              </p>
            </div>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-6" data-testid="gallery-grid">
              {galleryImages.map((image, index) => (
                <div
                  key={image.url}
                  className="mb-6 break-inside-avoid rounded-2xl overflow-hidden shadow-lg"
                  data-testid={`gallery-item-${index}`}
                >
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-auto object-cover"
                    data-testid={`gallery-image-${index}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-20 md:py-28 bg-slate-900 text-white" data-testid="contact-section">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="space-y-6" data-testid="contact-text-block">
              <p className="text-sm uppercase tracking-[0.35em] font-bold text-sky-300" data-testid="contact-eyebrow">
                Get Started
              </p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight" data-testid="contact-title">
               Need roof photos for an estimate today?

              </h2>
              <p className="text-base md:text-lg leading-relaxed text-slate-200" data-testid="contact-description">
                Contact us for availability, turnaround times, and project scheduling.
              </p>
              <div className="space-y-4" data-testid="contact-links">
                <a
                  href="tel:458-600-6028"
                  className="text-2xl font-semibold inline-flex items-center gap-3"
                  data-testid="contact-phone-link"
                >
                  <Phone size={22} className="text-sky-300" /> 458-600-6028
                </a>
                <a
                  href="mailto:info@cascaderoofimaging.com"
                  className="text-lg inline-flex items-center gap-3 text-slate-200"
                  data-testid="contact-email-link"
                >
                  <Mail size={20} className="text-sky-300" /> info@cascaderoofimaging.com
                </a>
              </div>
              <a
                href="mailto:info@cascaderoofimaging.com?subject=Cascade%20Roof%20Imaging%20Request"
                className="inline-flex items-center gap-2 bg-white text-slate-900 rounded-full px-8 py-4 font-semibold shadow-lg shadow-white/20 transition-all hover:scale-[1.02] active:scale-95"
                data-testid="contact-request-info-button"
              >
                Request Info <ArrowUpRight size={18} />
              </a>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 space-y-6" data-testid="contact-card">
              <div className="flex items-center gap-3" data-testid="contact-card-header">
                <ShieldCheck className="text-sky-300" />
                <p className="text-lg font-semibold" data-testid="contact-card-title">
                  What you can expect
                </p>
              </div>
              <ul className="space-y-4 text-slate-200" data-testid="contact-card-list">
                <li className="flex items-start gap-3" data-testid="contact-card-item-0">
                  <span className="h-2 w-2 rounded-full bg-sky-300 mt-2" />
                  Fast scheduling and delivery tailored to your job pipeline.
                </li>
                <li className="flex items-start gap-3" data-testid="contact-card-item-1">
                  <span className="h-2 w-2 rounded-full bg-sky-300 mt-2" />
                  Clear photo sets aligned to estimate and claim documentation needs.
                </li>
                <li className="flex items-start gap-3" data-testid="contact-card-item-2">
                  <span className="h-2 w-2 rounded-full bg-sky-300 mt-2" />
                  Local expertise for Bend & Central Oregon conditions.
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <footer className="py-10 bg-slate-950 text-slate-400" data-testid="site-footer">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="space-y-2" data-testid="footer-brand">
            <p className="text-white font-semibold" data-testid="footer-brand-name">
              Cascade Roof Imaging
            </p>
            <p className="text-sm" data-testid="footer-service-area">
              Serving Bend & Central Oregon
            </p>
          </div>
          <div className="text-sm" data-testid="footer-contact">
            <a
              href="https://www.cascaderoofimaging.com"
              className="text-slate-300 hover:text-white transition-colors"
              data-testid="footer-website-link"
            >
              www.cascaderoofimaging.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;